import React, { useState, useEffect, useCallback } from "react";
import _ from "lodash";
import classnames from "classnames/bind";
import {
  ISearchState as IState,
  ISearchVehicle as IVehicle,
  Replacement,
} from "../../../../interface";
import FilterDropdown from "../../FilterDropdown";
import FilterResult from "../../FilterResult";
import style from "./index.module.scss";
import instance from "../../../../services/httpRequest";
import { pushEvent } from "../../../../services/googleAnalytics";
import Loading from "../../../Loading";
import localesReplacement from '../../../../utils/localesReplacement';
import UsStates from "../../../../utils/states";
import AusStates from "../../../../utils/ausStates";
import UkCountries from "../../../../utils/ukCountries";
import { useDebounce } from "use-debounce";
import {
  GoogleReCaptchaProvider,
  GoogleReCaptcha,
} from "react-google-recaptcha-v3";
import {
  ANALYTICS_EVENT,
  ANALYTICS_PAGE_TYPE,
  ANALYTICS_SEARCH_TYPE,
  ANALYTICS_USER_TYPE,
  APPNAME,
  ENABLED_LICENSE_PLATE_COUNTRIES,
  ENABLED_LICENSE_PLATE_COUNTRIES_WITH_TRUCK,
  FASTSCAN,
  LICENSE_PLATE_GEOCODED_STATE,
} from '../../../../constants';
import getStateFromGeoCode from '../../../../utils/getStateFromGeoCode';
import {isAU, isUS} from '../../../../utils';
import {connect} from 'react-redux';
import {Dispatch} from 'redux';
import actions from '../../../../redux/actions';
import {useSearchParams} from 'react-router-dom';
import {useAppName} from '../../../../hooks/use-app-names';
import {useMediaQuery} from 'react-responsive';
import {useAnalytics} from '../../../../hooks/use-analytics';

import VehicleList from '../VehicleList';

export interface IProps {
  replacements: Replacement;
  onVehicleSelect?: (
    value: string | number,
    model: string,
    vehicleType?: string
  ) => void;
  selectedWorkshop?: boolean;
  country: string;
  language: string | undefined;
  captcha: boolean;
  licensePlateRegexes?: any;
  setVehiclesResults: (v: IVehicle[] | null) => void;
  setLicenseKeys: (v: string) => void;
  setLoading: (v: boolean) => void;
}

const VEHICLE_CATEGORIES_DATA = [
  {
    id: 1,
    name: 'Cars'
  },
  {
    id: 2,
    name: 'Trucks'
  }
];

const cx = classnames.bind(style);
 
const caption = (country?: string) => {
  if (country === 'GBR') {
    return 'Please specify your country.';
  }
  return isAU(country) || isUS(country)
    ? `Select your state and specify your license plate`
    : `Please specify your license plate.`;
};

// eslint-disable-next-line sonarjs/cognitive-complexity
function LicensePlateSearch(props: IProps) {
  const {
    onVehicleSelect,
    replacements,
    selectedWorkshop,
    country,
    captcha,
    language,
    licensePlateRegexes,
    setVehiclesResults,
    setLicenseKeys,
    setLoading,
  } = props;
  const sendAnalytics = useAnalytics();
  const isDesktopOrLaptop = useMediaQuery({ minWidth: 1024 });
  const isFastScan = useAppName(FASTSCAN);
  const [searchParams, setSearchParams] = useSearchParams();
  const [states, setStates] = useState<IState[]>([]);
  const [state, setState] = useState<string>("");
  const [licensePlate, setLicensePlate] = useState<string>("");
  const [truckLicensePlate, setTruckLicensePlate] = useState<string>("");
  const [results, setResults] = useState<IVehicle[]>([]);
  const [isMount, setIsMount] = useState<boolean>(false);
  const [finish, setFinish] = useState<boolean>(false);
  const [debounceValue] = useDebounce(licensePlate, 1000);
  const [debounceValue1] = useDebounce(truckLicensePlate, 1000);
  const [reCaptchaToken, setRecaptchaToken] = useState<string>("");
  const [refreshReCaptcha, setRefreshReCaptcha] = useState(false);
  const [submitted, setSubmitted] = useState<boolean>(false);
  const [invalidLicensePlate, setInvalidLicensePlate] =
    useState<boolean>(false);
  const [hsnTooltipOpen, sethsnTooltipOpen] = useState<boolean>(false);
  const [licensePlateCategory, setLicensePlateCategory] = useState<number>(1);

  useEffect(() => {
    setLicensePlate(_.toString(debounceValue));
  }, [debounceValue]);
 
  useEffect(() => {
    setTruckLicensePlate(_.toString(debounceValue1));
  }, [debounceValue1]);
 
  const onVerify = useCallback((token: any): void => {
    setRecaptchaToken(token);
  }, []);
  const switchStates = (country?: string) => {
    switch (country) {
      case "UAS":
        setStates(UsStates);
        break;
      case "AUS":
        setStates(AusStates);
        break;
      case "GBR":
        setStates(UkCountries);
        break;
      default:
        setStates(UsStates);
        break;
    }
  };
  const TRANSLATED_VEHICLE_CATEGORIES_DATA = VEHICLE_CATEGORIES_DATA.map(category => ({
    ...category,
    name: localesReplacement(replacements, category.name)
  }));
  const setCountryState = (result: any) => {
    let countryState;
    for (const statee of UsStates) {
      if (statee.id === result.isoCode) {
        countryState = statee.name;
      }
    }
    if (countryState) {
      setState(countryState);
    }
  };
 
  //Get list of US States
  useEffect(() => {
    switchStates(country);
    if (
      navigator.geolocation &&
      LICENSE_PLATE_GEOCODED_STATE.includes(country as string)
    ) {
      navigator.geolocation.getCurrentPosition((position) => {
        const { latitude, longitude } = position.coords;
        getStateFromGeoCode(latitude, longitude).then((result: any) => {
          if (result?.isoCode) {
            setCountryState(result);
          }
        });
      });
    }
  }, [country]);
 
  const getSearchPlates = () => {
    const isTruck = licensePlateCategory === 2;
    const text = isTruck ? `${licensePlate}🚚` : licensePlate;
    instance
      .get(
        `/usaLicensePlateSearch?text=${text}&stateCode=${
          states.find((x: {name: any}) => x.name === state)?.id || 'STA' // NOTE: Dummy state for other countries which doesn't need a state parameter
        }`,
      )
      .then((d: { status: any; data: any }) => {
        if (_.inRange(d.status, 200, 300)) {
          if (!_.isString(d.data)) {
            setResults(d.data);
          }
        } else {
          setResults([]);
        }
        setIsMount(false);
        setFinish(true);
        setRefreshReCaptcha(true);
        setSubmitted(false);
        setInvalidLicensePlate(false);
        setLoading(false);
      })
      .catch(() => {
        setIsMount(false);
        setFinish(true);
        setResults([]);
        setLoading(false);
      });
  };
 
  const dealWithError = () => {
    let isError = true;
 
    if (licensePlateRegexes) {
      for (let element = 0; element < licensePlateRegexes.length; element++) {
        if (licensePlate.match(licensePlateRegexes[element])) {
          isError = false;
        }
      }
    }
    return isError;
  };
 
  const captchaHandler = () => {
    if (captcha) {
      if (!reCaptchaToken) {
        setResults([]);
        return;
      }
      _.assign(instance.defaults.headers, {
        Authorization: `Captcha ${reCaptchaToken}`,
      });
    } else {
      _.unset(instance.defaults.headers, "Authorization");
    }
  };
 
  // Search  by license plate
  useEffect(() => {
    if (!submitted) {
      return;
    }    
    
    const isError = dealWithError();
 
    if (isError) {
      setResults([]);
      setIsMount(false);
      setFinish(true);
      setRefreshReCaptcha(true);
      setSubmitted(false);
      setInvalidLicensePlate(true);
      return;
    }
    // If It's enabled with state
    if (!state && ENABLED_LICENSE_PLATE_COUNTRIES.includes(country || "")) {
      setFinish(false);
      setResults([]);
      setSubmitted(false);
      setInvalidLicensePlate(false);
      return;
    }
 
    if (_.trim(licensePlate).length >= 3) {
      setLoading(true);
      setVehiclesResults([]);
      setInvalidLicensePlate(false);
      setFinish(false);
      setIsMount(true);
      captchaHandler();
      getSearchPlates();
 
      // Capture event for license plate search with or without success of the api
      sendAnalytics({
        event: ANALYTICS_EVENT.LICENSE_PLATE_SEARCH,
        search: ANALYTICS_SEARCH_TYPE.LICENSE_PLATE_SEARCH,
        page: ANALYTICS_PAGE_TYPE.PRODUCT_FINDER,
        query: licensePlate,
        user: selectedWorkshop
          ? ANALYTICS_USER_TYPE.WORKSHOP_OWNER
          : ANALYTICS_USER_TYPE.VEHICLE_OWNER,
        country: country,
      });
    } else {
      setInvalidLicensePlate(false);
      setFinish(false);
      setResults([]);
    }
  }, [submitted]);
 
  const onChangeHandler = (value: string) => {
    const upper = _.toUpper(value);
    if(value!==""){
      setTruckLicensePlate("");
    }
    setLicensePlate(upper);
    setFinish(false);
    setInvalidLicensePlate(false);
  };
  const onTruckChangeHandler = (value: string) => {
    const upper = _.toUpper(value);
    if(value!==""){
      setLicensePlate("");
    }
    setTruckLicensePlate(upper);
    setFinish(false);
    setInvalidLicensePlate(false);
  };
  const onClearHandler = () => {
    setResults([]);
    setSubmitted(false);
    setLicensePlate("");
    setInvalidLicensePlate(false);
  };
  const onTruckClearHandler = () => {
    setResults([]);
    setSubmitted(false);
    setTruckLicensePlate("");
    setInvalidLicensePlate(false);
  };
  /* State dropdown change */
  // eslint-disable-next-line
  const changeState = (stateCode: any) => {
    setState(stateCode);
  };
 
  const renderResults = () => {
    return !_.isEmpty(results) ? (
      <>
        <FilterResult
          dataList={results}
          sort={true}
          value={licensePlate}
          onSelect={onVehicleSelect}
          replacements={replacements}
        />
      </>
    ) : (
      finish && _.trim(licensePlate).length >= 3 && (
        <div className="no-match">
          <div className="no-found-text no-oil-text">
            <p>
              {!invalidLicensePlate
                ? localesReplacement(replacements, "No results found for")
                : localesReplacement(
                    replacements,
                    "Invalid License Plate number"
                  )}
              <strong>{_.trim(licensePlate)}</strong>
            </p>
          </div>
        </div>
      )
    );
  };
 
  const inputClassName = ENABLED_LICENSE_PLATE_COUNTRIES.includes(country || "")
    ? `${cx("search-input")}`
    : `${cx("search-input")} ${cx("fluid")}`;
 
  useEffect(() => {
    if (!_.isEmpty(results)) {
      setLicenseKeys("");
      setVehiclesResults(null);
    } else {
      if (finish && _.trim(licensePlate).length >= 3) {
        setLicenseKeys(
          !invalidLicensePlate
            ? `${localesReplacement(
                replacements,
                "No results found for"
              )} <strong>${_.trim(licensePlate)}</strong>`
            : localesReplacement(replacements, "Invalid License Plate number")
        );
      } else {
        setLicenseKeys("");
        setVehiclesResults(null);
      }
    }
    if (results && !_.isEmpty(results)) {
      setVehiclesResults(results);
    }
  }, [finish, licensePlate, invalidLicensePlate, results]);
 
  useEffect(() => {
    return () => {
      setLicenseKeys("");
      setVehiclesResults(null);
    };
  }, []);
 
  return (
    <>
      <div className={cx('dropdown-list')}>
        {ENABLED_LICENSE_PLATE_COUNTRIES_WITH_TRUCK.includes(country || '') && (
          <>
            { country === 'GBR' && (
              <>
                <p className={cx('caption-top')}>{caption(country)}</p>
                <div className={cx('dropdown-item')}
                  style={{paddingBottom: state !== 'Ireland' ? 0 : 'revert-layer'}} >
                  <FilterDropdown
                    dataList={states}
                    filter={state}
                    changeHandler={changeState}
                    type={
                      country === 'GBR'
                        ? localesReplacement(replacements, 'Country')
                        : localesReplacement(replacements, 'State')
                    }
                    replacements={replacements}
                    placeholder={country === 'GBR' ? 'Type or select country' : 'Type or select state'}
                  />
                </div>                
              </>
            )}
            { state !== 'Ireland' && (
              <div className={cx('search-category')}>
                <p className={cx('caption-vehicle-type')}>{localesReplacement(replacements, 'Please specify vehicle type')}</p>
                <VehicleList
                  selected={licensePlateCategory}
                  categoriesType={'Cars'}
                  changeVehicleTypeHandler={(i: any) => setLicensePlateCategory(i)}
                  dataList={TRANSLATED_VEHICLE_CATEGORIES_DATA} 
                  />
              </div>                          
            )}
          </>
        )}        
        <div className={cx('license')}>
          <div className={cx('corner', 't')}></div>
          <div className={cx('corner', 'b')}></div>
          <span
            className={cx("state", country, {
              hasValue: !!licensePlate.length,
            })}
          >
            {state ? state : `Your state name`}
          </span>
          <div className={inputClassName}>
            {captcha && (
              <>
                <div
                  id="captcha-container"
                  className={cx("recaptcha-container")}
                ></div>
                <GoogleReCaptchaProvider
                  reCaptchaKey={
                    process.env.REACT_APP_RECAPCHA_SITEKEY ||
                    "6Ld2oX4UAAAAACqWA9-3ostCypVeQRd4JPIT6dZR"
                  }
                  language={language?.slice(0, -1).toLowerCase()}
                  useRecaptchaNet={false}
                  useEnterprise={false}
                  scriptProps={{
                    async: false, // optional, default to false,
                    defer: false, // optional, default to false
                    appendTo: "head", // optional, default to "head", can be "head" or "body",
                    nonce: undefined, // optional, default undefined
                  }}
                  container={{
                    // optional to render inside custom element
                    element: "captcha-container",
                    parameters: {
                      badge: "inline", // optional, default undefined
                      theme: "dark", // optional, default undefined
                    },
                  }}
                >
                  <GoogleReCaptcha
                    onVerify={onVerify}
                    refreshReCaptcha={refreshReCaptcha}
                  />
                </GoogleReCaptchaProvider>
              </>
            )}           
            <input
              type="text"
              value={licensePlate}
              onChange={(ev: { target: { value: string } }) =>
                onChangeHandler(ev.target.value)
              }
              placeholder={localesReplacement(
                replacements,
                 `${country?.slice(0, 2)}123AB` 
              )}
              maxLength={36}
              data-testid="license-plate-input"
            />
            {!!licensePlate.length && (
              <i className={cx("remove")} onClick={onClearHandler}></i>
            )}
            {country === "DEU" && (
              <i
                className={cx("hsn")}
                onMouseEnter={() => {
                  if (!hsnTooltipOpen) {
                    sethsnTooltipOpen(true);
                  }
                }}
                onMouseOut={() => {
                  sethsnTooltipOpen(false);
                }}
              ></i>
            )}
            {hsnTooltipOpen && <div className={cx("hsn_tooltip")}></div>}
            <i className={cx("voice")}></i>
          </div>
        </div>
        {ENABLED_LICENSE_PLATE_COUNTRIES.includes(country || "") && (
          <>
            <p className={cx("caption")}>{caption(country)}</p>
            <div className={cx("dropdown-item")}>
              <FilterDropdown
                dataList={states}
                filter={state}
                changeHandler={changeState}
                type={
                  country === "GBR"
                    ? localesReplacement(replacements, "Country")
                    : localesReplacement(replacements, "State")
                }
                
                replacements={replacements}
                placeholder="Type or select state"
              />            
            </div>
          </>
        )}
        <div className={cx("results")}>
          {!isMount ? renderResults() : <Loading />}
        </div>
        <button
          className={style.submit}
          onClick={() => {
            setSubmitted(true);
            if (isDesktopOrLaptop) {
              searchParams.delete("id");
              searchParams.set("page", "landing");
              isFastScan && searchParams.set(APPNAME, FASTSCAN);
              setSearchParams(searchParams);
            }
          }}
        >
          {localesReplacement(replacements, "Search")}
        </button>
      </div>
    </>
  );
}
 
const mapDispatchToProps = (dispatch: Dispatch) => ({
  setVehiclesResults: (vehicle: IVehicle[] | null) =>
    dispatch(actions.setResults(vehicle)),
  setLicenseKeys: (v: string) => dispatch(actions.setLicenseKeys(v)),
  setLoading: (v: boolean) => dispatch(actions.setLoading(v)),
});
 
export default connect(null, mapDispatchToProps)(LicensePlateSearch);
